import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import {
  LOCALOPS_OPS_LAST_TASK_ID_KEY,
  canViewLocalOpsOps,
} from '../../utils/localOpsOpsAccess';
import './localOpsOpsHealth.css';

const STATUS_LABELS = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
  idle: 'Not checked',
};

const formatJgk = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0 JGK';
  return `${Math.round(amount).toLocaleString('en-US')} JGK`;
};

const formatUsd = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '--';
  return `$${amount.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return 'Not checked';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const truthyLabel = (value) => {
  if (value === true) return 'Pass';
  if (value === false) return 'Fail';
  return 'Unknown';
};

const TASK_STATUS_OPTIONS = [
  '',
  'created',
  'discoverable',
  'accepted',
  'in_progress',
  'completed',
  'cancelled',
  'expired',
];

const LAUNCH_ATTRIBUTION_ENDPOINT =
  'local-ops/debug/camden-launch-attribution';
const CONCIERGE_INTAKE_ENDPOINT =
  'local-ops/intake-drafts/collision-specialist-ride/send-magic-link';

const COLLISION_SPECIALIST_PICKUP_ADDRESS =
  'Collision Specialist, 134 West Main Street, Camden, TN 38320';

const buildDefaultConciergeIntakeForm = () => ({
  deliveryAddress: '',
  customerEmail: '',
  customerPhone: '',
  notes: '',
});

const CAMDEN_LAUNCH_ATTRIBUTION_PARAMS = Object.freeze({
  regionSlug: 'camden',
  marketSlug: 'camden',
});

const LAUNCH_EVENT_LABELS = Object.freeze({
  localops_task_published: 'Task posts',
  localops_task_accepted: 'Task accepts',
  localops_task_completion_verified: 'Task completions',
  localops_action_credit_awarded: 'Action Credit',
  localops_offering_published: 'Offering posts',
  localops_offering_reserved: 'Offering reservations',
  localops_offering_confirmation_requested: 'Confirmation requested',
  localops_offering_settlement_resolved: 'Offering settlements',
});

const LAUNCH_EVENT_ORDER = [
  'localops_task_published',
  'localops_task_accepted',
  'localops_task_completion_verified',
  'localops_action_credit_awarded',
  'localops_offering_published',
  'localops_offering_reserved',
  'localops_offering_confirmation_requested',
  'localops_offering_settlement_resolved',
];

const VISIT_EVENT_LABELS = Object.freeze({
  localops_region_landing_viewed: 'Landing views',
  localops_region_engaged_10s: '10s engaged',
  localops_region_engaged_60s: '60s engaged',
  localops_region_cta_clicked: 'CTA clicks',
});

const VISIT_EVENT_ORDER = [
  'localops_region_landing_viewed',
  'localops_region_engaged_10s',
  'localops_region_engaged_60s',
  'localops_region_cta_clicked',
];

const padDatePart = (value) => String(value).padStart(2, '0');

const toDateInputValue = (date) =>
  [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join('-');

const toLocalDateBoundaryIso = (value, boundary) => {
  if (!value) return '';
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return value;
  const date = new Date(year, month - 1, day);
  if (boundary === 'end') {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
};

export function buildDefaultTaskFinderFilters(now = new Date()) {
  const to = new Date(now);
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  return {
    playerId: '',
    q: '',
    from: toDateInputValue(from),
    to: toDateInputValue(to),
    status: '',
    boosted: false,
    rewarded: false,
    anomalyOnly: false,
    sort: 'newest',
    limit: '25',
  };
}

const buildTaskFinderParams = (filters) => {
  const params = {
    from: toLocalDateBoundaryIso(filters.from, 'start'),
    to: toLocalDateBoundaryIso(filters.to, 'end'),
    sort: filters.sort || 'newest',
    limit: filters.limit || '25',
  };
  if (filters.playerId) params.playerId = filters.playerId.trim();
  if (filters.q) params.q = filters.q.trim();
  if (filters.status) params.status = filters.status;
  if (filters.boosted) params.boosted = 'true';
  if (filters.rewarded) params.rewarded = 'true';
  if (filters.anomalyOnly) params.anomalyOnly = 'true';
  return params;
};

const formatCount = (value) => {
  const count = Number(value || 0);
  if (!Number.isFinite(count)) return '0';
  return Math.round(count).toLocaleString('en-US');
};

const getTotalLaunchEvents = (report) =>
  Object.values(report?.totalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalLaunchVisitEvents = (report) =>
  Object.values(report?.visitTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const hasDurableSourceAttribution = (row) =>
  !!(
    row?.source ||
    row?.src ||
    row?.partnerSource ||
    row?.partnerAction ||
    row?.shareId
  );

const getSourceLabel = (row) => {
  if (!hasDurableSourceAttribution(row)) return 'Unattributed';
  return row?.sourceKey || row?.src || row?.source || 'Unattributed';
};

const formatSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.regionSlug) parts.push(`region ${row.regionSlug}`);
  if (row?.marketSlug) parts.push(`market ${row.marketSlug}`);
  if (row?.outcome) parts.push(`outcome ${row.outcome}`);
  return parts.join(' / ');
};

const buildLaunchSourceRows = (report) => {
  const bySource = new Map();
  Object.entries(report?.eventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = getSourceLabel(row);
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatVisitSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.routeKind) parts.push(`route ${row.routeKind}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.ctaId) parts.push(`cta ${row.ctaId}`);
  return parts.join(' / ');
};

const buildVisitSourceRows = (report) => {
  if (Array.isArray(report?.visitSourceBreakdown)) {
    return report.visitSourceBreakdown
      .map((row) => ({
        sourceLabel: row?.sourceKey || row?.src || row?.source || 'direct',
        details: formatVisitSourceDetails(row),
        counts: row?.counts || {},
        total: Number(row?.total || 0),
      }))
      .sort(
        (a, b) =>
          b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
      );
  }

  const bySource = new Map();
  Object.entries(report?.visitEventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = row?.sourceKey || row?.src || row?.source || 'direct';
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatVisitSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatActionCreditCoverage = (coverage) => {
  const total = Number(coverage?.total || 0);
  if (!total) return 'No Action Credit events';
  const attributed = Number(coverage?.attributed || 0);
  const rawCoverage = Number(coverage?.coverage);
  const percent = Number.isFinite(rawCoverage)
    ? `${(rawCoverage * 100).toFixed(1).replace(/\.0$/, '')}%`
    : 'Unknown';
  return `${formatCount(attributed)} of ${formatCount(total)} attributed (${percent})`;
};

const addReadinessAnomalies = (items, readiness) => {
  const summary = readiness?.summary || {};
  (summary.failures || []).forEach((failure) => {
    items.push({
      source: 'readiness',
      severity: 'error',
      code: failure,
      message: failure,
    });
  });
  (summary.warnings || []).forEach((warning) => {
    items.push({
      source: 'readiness',
      severity: 'warning',
      code: warning,
      message: warning,
    });
  });
};

const addRollupAnomalies = (items, rollup) => {
  const duplicateKeys = rollup?.duplicateIdempotencyKeys || [];
  duplicateKeys.forEach((key) => {
    items.push({
      source: 'rollup',
      severity: 'warning',
      code: 'duplicate_idempotency_key',
      message: `Duplicate idempotency key: ${key}`,
    });
  });

  const missingRefs = Number(rollup?.missingSourceTransactionRefCount || 0);
  if (missingRefs > 0) {
    items.push({
      source: 'rollup',
      severity: 'warning',
      code: 'missing_source_transaction_ref',
      message: `${missingRefs} journal entries are missing source transaction references.`,
    });
  }
};

const addTaskAnomalies = (items, taskReport) => {
  (taskReport?.anomalies || []).forEach((item) => {
    items.push({
      source: 'task',
      severity: item.severity || 'warning',
      code: item.code || 'task_anomaly',
      message: item.message || item.code || 'Task accounting anomaly',
    });
  });
};

export function collectLocalOpsOpsAnomalies({ readiness, rollup, taskReport }) {
  const items = [];
  addReadinessAnomalies(items, readiness);
  addRollupAnomalies(items, rollup);
  addTaskAnomalies(items, taskReport);
  return items;
}

export function deriveLocalOpsOpsStatus({ readiness, rollup, anomalies, error }) {
  if (error) return 'red';
  if (!readiness || !rollup) return 'idle';
  if (readiness?.summary?.pass === false || rollup?.pass === false) return 'red';
  if ((anomalies || []).some((item) => item.severity === 'error')) return 'red';
  if ((anomalies || []).length > 0) return 'yellow';
  return 'green';
}

const Metric = ({ label, value }) => (
  <div className="ops-health-metric">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const JsonDetails = ({ title, payload, testId }) => (
  <details className="ops-health-json" data-testid={testId}>
    <summary>{title}</summary>
    <pre>{JSON.stringify(payload || {}, null, 2)}</pre>
  </details>
);

export default function LocalOpsOpsHealth() {
  const { player } = useContext(AuthContext);
  const allowed = canViewLocalOpsOps(player);
  const [readiness, setReadiness] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [taskFilters, setTaskFilters] = useState(() =>
    buildDefaultTaskFinderFilters(),
  );
  const [taskResults, setTaskResults] = useState([]);
  const [taskReport, setTaskReport] = useState(null);
  const [settlementRows, setSettlementRows] = useState([]);
  const [taskId, setTaskId] = useState('');
  const [loading, setLoading] = useState(false);
  const [taskSearchLoading, setTaskSearchLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementActionBusy, setSettlementActionBusy] = useState('');
  const [error, setError] = useState('');
  const [taskSearchError, setTaskSearchError] = useState('');
  const [taskError, setTaskError] = useState('');
  const [settlementError, setSettlementError] = useState('');
  const [settlementNotice, setSettlementNotice] = useState('');
  const [launchAttribution, setLaunchAttribution] = useState(null);
  const [launchAttributionLoading, setLaunchAttributionLoading] = useState(false);
  const [launchAttributionError, setLaunchAttributionError] = useState('');
  const [conciergeForm, setConciergeForm] = useState(() =>
    buildDefaultConciergeIntakeForm(),
  );
  const [conciergeSending, setConciergeSending] = useState(false);
  const [conciergeError, setConciergeError] = useState('');
  const [conciergeResult, setConciergeResult] = useState(null);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );

  useEffect(() => {
    if (!allowed) return;
    try {
      const remembered = window.localStorage.getItem(
        LOCALOPS_OPS_LAST_TASK_ID_KEY,
      );
      if (remembered) setTaskId((current) => current || remembered);
    } catch (err) {
      // Ignore localStorage issues; this page should still run diagnostics.
    }
  }, [allowed]);

  const refreshReports = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const [readinessRes, rollupRes] = await Promise.all([
        axiosConn.get('local-ops/debug/readiness', authConfig),
        axiosConn.get('local-ops/debug/accounting/rollup', authConfig),
      ]);
      setReadiness(readinessRes?.data?.data || readinessRes?.data || null);
      setRollup(rollupRes?.data?.data || rollupRes?.data || null);
      setLastCheckedAt(new Date().toISOString());
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load LocalOps ops health.',
      );
    } finally {
      setLoading(false);
    }
  }, [allowed, authConfig]);

  const fetchSettlementQueue = useCallback(async () => {
    if (!allowed) return;
    setSettlementLoading(true);
    setSettlementError('');
    try {
      const res = await axiosConn.get(
        'local-ops/debug/settlements/orders',
        authConfig,
      );
      const payload = res?.data?.data || res?.data || {};
      setSettlementRows(payload.rows || []);
    } catch (err) {
      setSettlementRows([]);
      setSettlementError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load offering settlement queue.',
      );
    } finally {
      setSettlementLoading(false);
    }
  }, [allowed, authConfig]);

  const fetchLaunchAttribution = useCallback(async () => {
    if (!allowed) return;
    setLaunchAttributionLoading(true);
    setLaunchAttributionError('');
    try {
      const res = await axiosConn.get(LAUNCH_ATTRIBUTION_ENDPOINT, {
        ...authConfig,
        params: CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
      });
      setLaunchAttribution(res?.data?.data || res?.data || null);
    } catch (err) {
      setLaunchAttribution(null);
      setLaunchAttributionError('Unable to load launch attribution.');
    } finally {
      setLaunchAttributionLoading(false);
    }
  }, [allowed, authConfig]);

  const fetchTaskResults = useCallback(
    async (filters) => {
      if (!allowed) return;
      setTaskSearchLoading(true);
      setTaskSearchError('');
      try {
        const res = await axiosConn.get('local-ops/debug/accounting/tasks', {
          ...authConfig,
          params: buildTaskFinderParams(filters),
        });
        const payload = res?.data?.data || res?.data || {};
        setTaskResults(payload.tasks || payload.rows || []);
      } catch (err) {
        setTaskResults([]);
        setTaskSearchError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to find recent LocalOps tasks.',
        );
      } finally {
        setTaskSearchLoading(false);
      }
    },
    [allowed, authConfig],
  );

  const refreshAll = useCallback(() => {
    if (!allowed) return;
    refreshReports();
    fetchSettlementQueue();
    fetchLaunchAttribution();
    fetchTaskResults(taskFilters);
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchSettlementQueue,
    fetchTaskResults,
    refreshReports,
    taskFilters,
  ]);

  useEffect(() => {
    if (!allowed) return;
    refreshReports();
    fetchSettlementQueue();
    fetchLaunchAttribution();
    fetchTaskResults(taskFilters);
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchSettlementQueue,
    fetchTaskResults,
    refreshReports,
  ]);

  const loadTaskReport = useCallback(
    async (nextTaskId) => {
      if (!allowed) return;
      const trimmedTaskId = String(nextTaskId || '').trim();
      if (!trimmedTaskId) {
        setTaskError('Enter a task id.');
        return;
      }

      setTaskId(trimmedTaskId);
      setTaskLoading(true);
      setTaskError('');
      try {
        const res = await axiosConn.get(
          `local-ops/debug/accounting/task/${encodeURIComponent(trimmedTaskId)}`,
          authConfig,
        );
        setTaskReport(res?.data?.data || res?.data || null);
        try {
          window.localStorage.setItem(
            LOCALOPS_OPS_LAST_TASK_ID_KEY,
            trimmedTaskId,
          );
        } catch (err) {
          // Last-task memory is optional and must never block the report.
        }
      } catch (err) {
        setTaskReport(null);
        setTaskError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to load task accounting report.',
        );
      } finally {
        setTaskLoading(false);
      }
    },
    [allowed, authConfig],
  );

  const handleTaskSearch = (event) => {
    event.preventDefault();
    if (!allowed) return;
    fetchTaskResults(taskFilters);
  };

  const handleTaskLookup = async (event) => {
    event.preventDefault();
    await loadTaskReport(taskId);
  };

  const updateTaskFilter = (field, value) => {
    setTaskFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSettlementAction = async (row, action) => {
    if (!allowed || !row?.orderId) return;
    const isRefund = action === 'refund_buyer';
    const confirmation = isRefund
      ? 'Refund buyer? This returns held JGK to the buyer and restores finite offering quantity if applicable.'
      : 'Pay seller? This releases held JGK to the seller and marks the order fulfilled.';
    const confirmed = window.confirm(confirmation);
    if (!confirmed) return;

    setSettlementActionBusy(`${row.orderId}:${action}`);
    setSettlementError('');
    setSettlementNotice('');
    try {
      await axiosConn.post(
        `local-ops/debug/settlements/orders/${encodeURIComponent(
          row.orderId,
        )}/resolve`,
        {
          action,
          reason: 'ops_health_settlement_queue',
        },
        authConfig,
      );
      setSettlementNotice(
        isRefund
          ? 'Buyer refunded; held JGK returned.'
          : 'Seller paid; order marked fulfilled.',
      );
      await fetchSettlementQueue();
    } catch (err) {
      setSettlementError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to resolve this settlement.',
      );
    } finally {
      setSettlementActionBusy('');
    }
  };

  const updateConciergeForm = (field, value) => {
    setConciergeForm((current) => ({
      ...current,
      [field]: value,
    }));
    setConciergeError('');
  };

  const handleConciergeSubmit = async (event) => {
    event.preventDefault();
    if (!allowed) return;
    const deliveryAddress = conciergeForm.deliveryAddress.trim();
    const customerEmail = conciergeForm.customerEmail.trim();
    if (!deliveryAddress) {
      setConciergeError('Destination is required.');
      return;
    }
    if (!customerEmail) {
      setConciergeError('Customer email is required.');
      return;
    }

    setConciergeSending(true);
    setConciergeError('');
    setConciergeResult(null);
    try {
      const res = await axiosConn.post(
        CONCIERGE_INTAKE_ENDPOINT,
        {
          intakeType: 'collision_specialist_ride',
          partnerSource: 'collision-specialist-camden',
          customerEmail,
          customerPhone: conciergeForm.customerPhone.trim() || undefined,
          deliveryAddress,
          notes: conciergeForm.notes.trim() || undefined,
        },
        authConfig,
      );
      setConciergeResult(res?.data?.data || res?.data || {});
      setConciergeForm((current) => ({
        ...buildDefaultConciergeIntakeForm(),
        customerPhone: current.customerPhone,
      }));
    } catch (err) {
      setConciergeError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to send the concierge magic link.',
      );
    } finally {
      setConciergeSending(false);
    }
  };

  const anomalies = useMemo(
    () => collectLocalOpsOpsAnomalies({ readiness, rollup, taskReport }),
    [readiness, rollup, taskReport],
  );
  const opsStatus = deriveLocalOpsOpsStatus({
    readiness,
    rollup,
    anomalies,
    error,
  });
  const launchTotalEvents = getTotalLaunchEvents(launchAttribution);
  const launchVisitTotalEvents = getTotalLaunchVisitEvents(launchAttribution);
  const launchSourceRows = useMemo(
    () => buildLaunchSourceRows(launchAttribution),
    [launchAttribution],
  );
  const launchVisitSourceRows = useMemo(
    () => buildVisitSourceRows(launchAttribution),
    [launchAttribution],
  );

  if (!allowed) {
    return (
      <main className="ops-health-page ops-health-page--locked">
        <section className="ops-health-locked" data-testid="ops-health-locked">
          <h1>Ops Health unavailable</h1>
          <p>This internal LocalOps report is not available for this account.</p>
        </section>
      </main>
    );
  }

  const readinessSummary = readiness?.summary || {};
  const staticConfig = readiness?.staticConfig || {};
  const database = readiness?.database || {};
  const stripe = staticConfig?.stripe || {};
  const ignitionFlags = staticConfig?.ignition?.flags || {};
  const systemPlayers = database?.systemPlayers || {};

  return (
    <main className="ops-health-page" data-testid="ops-health-page">
      <header className="ops-health-header">
        <div>
          <p className="ops-health-kicker">LocalOps Internal</p>
          <h1>Ops Health</h1>
        </div>
        <button
          type="button"
          className="ops-health-refresh"
          onClick={refreshAll}
          disabled={loading || taskSearchLoading || launchAttributionLoading}
        >
          {loading || taskSearchLoading || launchAttributionLoading
            ? 'Refreshing...'
            : 'Refresh'}
        </button>
      </header>

      <section
        className="ops-health-panel ops-health-concierge"
        data-testid="ops-health-concierge-intake"
      >
        <div className="ops-health-section-heading">
          <h2>LocalOps Concierge Intake</h2>
          <span>collision-specialist-camden</span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Create a customer-owned ride draft and send the existing magic-link
          handoff. The customer reviews, funds, and publishes the task.
        </p>
        <form
          className="ops-health-intake-form"
          onSubmit={handleConciergeSubmit}
        >
          <label htmlFor="ops-health-intake-partner">Partner source</label>
          <input
            id="ops-health-intake-partner"
            value="Collision Specialist"
            readOnly
          />

          <label htmlFor="ops-health-intake-pickup">Pickup</label>
          <input
            id="ops-health-intake-pickup"
            value={COLLISION_SPECIALIST_PICKUP_ADDRESS}
            readOnly
          />

          <label htmlFor="ops-health-intake-destination">Destination</label>
          <input
            id="ops-health-intake-destination"
            value={conciergeForm.deliveryAddress}
            onChange={(event) =>
              updateConciergeForm('deliveryAddress', event.target.value)
            }
            placeholder="Customer destination address"
            required
          />

          <label htmlFor="ops-health-intake-email">Customer email</label>
          <input
            id="ops-health-intake-email"
            type="email"
            value={conciergeForm.customerEmail}
            onChange={(event) =>
              updateConciergeForm('customerEmail', event.target.value)
            }
            placeholder="customer@example.com"
            required
          />

          <label htmlFor="ops-health-intake-phone">Customer phone</label>
          <input
            id="ops-health-intake-phone"
            value={conciergeForm.customerPhone}
            onChange={(event) =>
              updateConciergeForm('customerPhone', event.target.value)
            }
            placeholder="Optional manual reference"
          />

          <label htmlFor="ops-health-intake-notes">Notes</label>
          <textarea
            id="ops-health-intake-notes"
            value={conciergeForm.notes}
            onChange={(event) =>
              updateConciergeForm('notes', event.target.value)
            }
            placeholder="Optional internal note"
          />

          <div className="ops-health-intake-price">
            <span>Suggested price</span>
            <strong>
              {Number.isFinite(Number(conciergeResult?.suggestedPriceUsd))
                ? formatUsd(conciergeResult.suggestedPriceUsd)
                : 'Auto after destination is resolved'}
            </strong>
          </div>

          <div className="ops-health-intake-actions">
            <button type="submit" disabled={conciergeSending}>
              {conciergeSending ? 'Sending...' : 'Send magic link'}
            </button>
          </div>
        </form>
        {conciergeError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            {conciergeError}
          </div>
        ) : null}
        {conciergeResult ? (
          <div
            className="ops-health-alert ops-health-alert--success ops-health-alert--inline"
            data-testid="ops-health-concierge-success"
          >
            <strong>Magic link sent.</strong>
            <p>
              Suggested amount{' '}
              {formatUsd(conciergeResult.suggestedPriceUsd)}. Draft token
              created: {conciergeResult.draftTokenCreated ? 'yes' : 'unknown'}.
            </p>
            {conciergeResult.fallbackReviewLink ? (
              <p>
                Review link:{' '}
                <code data-testid="ops-health-concierge-review-link">
                  {conciergeResult.fallbackReviewLink}
                </code>
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      <section
        className={`ops-health-status ops-health-status--${opsStatus}`}
        data-testid="ops-health-status"
      >
        <div>
          <span className="ops-health-status-label">Overall status</span>
          <strong>{STATUS_LABELS[opsStatus]}</strong>
        </div>
        <Metric label="Last checked" value={formatDate(lastCheckedAt)} />
        <Metric label="Readiness" value={truthyLabel(readinessSummary.pass)} />
        <Metric label="Accounting" value={truthyLabel(rollup?.pass)} />
        <Metric label="Anomalies" value={anomalies.length} />
        <Metric label="Journal entries" value={rollup?.entryCount ?? '--'} />
        <Metric
          label="Rollup period"
          value={
            rollup?.from || rollup?.to
              ? `${rollup?.from || 'start'} to ${rollup?.to || 'now'}`
              : 'All time'
          }
        />
      </section>

      {error ? (
        <section className="ops-health-alert ops-health-alert--error">
          {error}
        </section>
      ) : null}

      <section className="ops-health-panel ops-health-anomalies">
        <div className="ops-health-section-heading">
          <h2>Anomalies</h2>
          <span>{anomalies.length}</span>
        </div>
        {anomalies.length > 0 ? (
          <ul>
            {anomalies.map((item, index) => (
              <li
                key={`${item.source}-${item.code}-${index}`}
                className={`ops-health-anomaly ops-health-anomaly--${item.severity}`}
              >
                <span>{item.severity}</span>
                <strong>{item.code}</strong>
                <p>{item.message}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ops-health-muted">No anomalies reported by the current checks.</p>
        )}
      </section>

      <section
        className="ops-health-panel ops-health-launch-attribution"
        data-testid="ops-health-launch-attribution"
      >
        <div className="ops-health-section-heading">
          <h2>Camden Launch Attribution</h2>
          <span>{launchAttribution?.proofType || 'durable events'}</span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Durable attribution from LocalOps events. Shows which launch sources are
          creating visits, CTA clicks, posts, accepts, reservations,
          completions, settlements, and Action Credit.
        </p>
        {launchAttribution?.generatedAt ? (
          <p className="ops-health-mini-meta">
            Generated {formatDate(launchAttribution.generatedAt)}
            {launchAttribution?.filters?.regionSlug
              ? ` / region ${launchAttribution.filters.regionSlug}`
              : ''}
            {launchAttribution?.filters?.marketSlug
              ? ` / market ${launchAttribution.filters.marketSlug}`
              : ''}
          </p>
        ) : null}
        {launchAttributionLoading ? (
          <p className="ops-health-muted">
            Loading Camden launch attribution...
          </p>
        ) : null}
        {launchAttributionError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            <strong>Unable to load launch attribution.</strong>
            <p>Refresh this report or check admin access if the issue persists.</p>
          </div>
        ) : null}
        {!launchAttributionLoading &&
        !launchAttributionError &&
        launchAttribution &&
        launchTotalEvents === 0 &&
        launchVisitTotalEvents === 0 ? (
          <div className="ops-health-empty">
            <p>No durable launch attribution events yet.</p>
            <p>
              New visits, CTA clicks, posts, accepts, reservations,
              settlements, and Action Credit events will appear here after they
              are recorded.
            </p>
          </div>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchVisitTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-visit-summary"
            >
              {VISIT_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={VISIT_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.visitTotalsByEvent?.[eventName],
                  )}
                />
              ))}
            </div>
            {launchVisitSourceRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for visit funnel</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      {VISIT_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {VISIT_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {launchVisitSourceRows.map((row) => (
                      <tr key={row.sourceLabel}>
                        <th scope="row">
                          <span>{row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                        </th>
                        {VISIT_EVENT_ORDER.map((eventName) => (
                          <td key={eventName}>
                            {formatCount(row.counts[eventName])}
                          </td>
                        ))}
                        <td>{formatCount(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError && launchAttribution && launchTotalEvents > 0 ? (
          <>
            <div className="ops-health-list ops-health-launch-summary">
              {LAUNCH_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={LAUNCH_EVENT_LABELS[eventName]}
                  value={formatCount(launchAttribution.totalsByEvent?.[eventName])}
                />
              ))}
              <Metric
                label="Unattributed canonical events"
                value={formatCount(
                  launchAttribution.unattributedCanonicalEventCount,
                )}
              />
              <Metric
                label="Source to Action Credit"
                value={formatActionCreditCoverage(
                  launchAttribution.actionCreditJoinCoverage,
                )}
              />
            </div>
            <div className="ops-health-table-wrap">
              <table className="ops-health-source-table">
                <caption>Source breakdown for canonical events</caption>
                <thead>
                  <tr>
                    <th scope="col">Source</th>
                    {LAUNCH_EVENT_ORDER.map((eventName) => (
                      <th key={eventName} scope="col">
                        {LAUNCH_EVENT_LABELS[eventName]}
                      </th>
                    ))}
                    <th scope="col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {launchSourceRows.map((row) => (
                    <tr key={row.sourceLabel}>
                      <th scope="row">
                        <span>{row.sourceLabel}</span>
                        {row.details ? <small>{row.details}</small> : null}
                      </th>
                      {LAUNCH_EVENT_ORDER.map((eventName) => (
                        <td key={eventName}>
                          {formatCount(row.counts[eventName])}
                        </td>
                      ))}
                      <td>{formatCount(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>

      <section
        className="ops-health-panel ops-health-settlement-queue"
        data-testid="ops-health-settlement-queue"
      >
        <div className="ops-health-section-heading">
          <h2>Offering Settlement Queue</h2>
          <span>{settlementRows.length} rows</span>
        </div>
        {settlementError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {settlementError}
          </div>
        ) : null}
        {settlementNotice ? (
          <div className="ops-health-alert ops-health-alert--success">
            {settlementNotice}
          </div>
        ) : null}
        {settlementLoading ? (
          <p className="ops-health-muted">Loading settlement queue...</p>
        ) : null}
        {!settlementLoading && settlementRows.length === 0 ? (
          <p className="ops-health-muted">
            No stuck offering settlements are waiting for admin action.
          </p>
        ) : null}
        {settlementRows.length > 0 ? (
          <div className="ops-health-settlement-results">
            {settlementRows.map((row) => {
              const rowKey = row.orderId || `${row.offeringTitle}-${row.updatedAt}`;
              const allowedActions = Array.isArray(row.allowedActions)
                ? row.allowedActions
                : [];
              const refundBusy =
                settlementActionBusy === `${row.orderId}:refund_buyer`;
              const payBusy =
                settlementActionBusy === `${row.orderId}:pay_seller`;
              return (
                <article
                  className="ops-health-settlement-row"
                  key={rowKey}
                  data-testid="ops-health-settlement-row"
                >
                  <div className="ops-health-task-row-main">
                    <strong>{row.offeringTitle || 'Untitled offering'}</strong>
                    <span>{row.orderId}</span>
                  </div>
                  <div className="ops-health-task-row-meta">
                    <span>{row.classification || 'Unclassified'}</span>
                    <span>buyer {row.buyer?.playerId || '--'}</span>
                    <span>seller {row.seller?.playerId || '--'}</span>
                    <span>qty {row.quantity ?? '--'}</span>
                    <span>{formatUsd(row.amountUsd)} / {formatJgk(row.amountJGK)}</span>
                  </div>
                  <div className="ops-health-task-row-badges">
                    <span>{row.status || 'unknown'} order</span>
                    <span>{row.escrowStatus || 'none'} escrow</span>
                    <span>{row.settlementStatus || 'none'} settlement</span>
                    <span>{row.settlementOutcome || 'none'} outcome</span>
                    <span>confirmed {formatDate(row.providerConfirmedAt)}</span>
                    <span>confirm by {formatDate(row.confirmByAt)}</span>
                    <span>updated {formatDate(row.updatedAt)}</span>
                  </div>
                  <div className="ops-health-settlement-actions">
                    {allowedActions.includes('refund_buyer') ? (
                      <button
                        type="button"
                        onClick={() => handleSettlementAction(row, 'refund_buyer')}
                        disabled={!!settlementActionBusy}
                      >
                        {refundBusy ? 'Refunding...' : 'Refund buyer'}
                      </button>
                    ) : null}
                    {allowedActions.includes('pay_seller') ? (
                      <button
                        type="button"
                        onClick={() => handleSettlementAction(row, 'pay_seller')}
                        disabled={!!settlementActionBusy}
                      >
                        {payBusy ? 'Paying...' : 'Pay seller'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        window.navigator?.clipboard?.writeText?.(row.orderId)
                      }
                    >
                      Copy order ID
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="ops-health-grid">
        <div className="ops-health-panel">
          <div className="ops-health-section-heading">
            <h2>Readiness</h2>
            <span>{truthyLabel(readinessSummary.pass)}</span>
          </div>
          <div className="ops-health-list">
            <Metric label="Env health" value={truthyLabel(staticConfig.pass)} />
            <Metric
              label="Escrow player"
              value={truthyLabel(systemPlayers.escrow?.exists)}
            />
            <Metric
              label="Treasury player"
              value={truthyLabel(systemPlayers.treasury?.exists)}
            />
            <Metric
              label="Stripe prices"
              value={truthyLabel(stripe.allPriceEnvsPresent)}
            />
            <Metric
              label="Stripe webhook"
              value={truthyLabel(stripe.webhookSecret?.present)}
            />
            <Metric
              label="Secrets redacted"
              value={truthyLabel(readinessSummary.secretsRedacted)}
            />
          </div>
          <div className="ops-health-flags">
            <span>Ignition</span>
            <strong>
              incentives {String(ignitionFlags.incentives?.effective ?? 'unknown')}
            </strong>
            <strong>
              rewards {String(ignitionFlags.rewards?.effective ?? 'unknown')}
            </strong>
            <strong>
              boost {String(ignitionFlags.boost?.effective ?? 'unknown')}
            </strong>
          </div>
        </div>

        <div className="ops-health-panel">
          <div className="ops-health-section-heading">
            <h2>Accounting Rollup</h2>
            <span>{rollup?.proofType || 'not loaded'}</span>
          </div>
          <div className="ops-health-list">
            <Metric
              label="Platform revenue"
              value={formatJgk(rollup?.platformRevenueJGK)}
            />
            <Metric
              label="Contra revenue / boosts"
              value={formatJgk(rollup?.contraRevenueJGK)}
            />
            <Metric
              label="Promo issuance / rewards"
              value={formatJgk(rollup?.promoIssuanceJGK)}
            />
            <Metric
              label="Helper payouts"
              value={formatJgk(rollup?.helperPayoutsJGK)}
            />
            <Metric label="Escrow held" value={formatJgk(rollup?.escrowHeldJGK)} />
            <Metric
              label="Escrow released"
              value={formatJgk(rollup?.escrowReleasedJGK)}
            />
            <Metric
              label="Cashout held"
              value={formatJgk(rollup?.cashoutHeldJGK)}
            />
            <Metric
              label="Cashout paid"
              value={formatJgk(rollup?.cashoutSettledJGK)}
            />
            <Metric
              label="Cashout released"
              value={formatJgk(rollup?.cashoutReleasedJGK)}
            />
            <Metric
              label="Net platform position"
              value={formatJgk(rollup?.netPlatformPositionJGK)}
            />
          </div>
        </div>
      </section>

      <section className="ops-health-panel ops-health-task-finder">
        <div className="ops-health-section-heading">
          <h2>Find Recent Tasks</h2>
          <span>{taskResults.length} results</span>
        </div>
        <form
          className="ops-health-finder-form"
          data-testid="ops-health-task-finder-form"
          onSubmit={handleTaskSearch}
        >
          <label htmlFor="ops-health-player-id">Player</label>
          <input
            id="ops-health-player-id"
            value={taskFilters.playerId}
            onChange={(event) => updateTaskFilter('playerId', event.target.value)}
            placeholder="creator or helper playerId"
          />

          <label htmlFor="ops-health-task-title">Title</label>
          <input
            id="ops-health-task-title"
            value={taskFilters.q}
            onChange={(event) => updateTaskFilter('q', event.target.value)}
            placeholder="title fragment"
          />

          <label htmlFor="ops-health-task-from">From</label>
          <input
            id="ops-health-task-from"
            type="date"
            value={taskFilters.from}
            onChange={(event) => updateTaskFilter('from', event.target.value)}
          />

          <label htmlFor="ops-health-task-to">To</label>
          <input
            id="ops-health-task-to"
            type="date"
            value={taskFilters.to}
            onChange={(event) => updateTaskFilter('to', event.target.value)}
          />

          <label htmlFor="ops-health-task-status">Status</label>
          <select
            id="ops-health-task-status"
            value={taskFilters.status}
            onChange={(event) => updateTaskFilter('status', event.target.value)}
          >
            {TASK_STATUS_OPTIONS.map((statusOption) => (
              <option key={statusOption || 'all'} value={statusOption}>
                {statusOption || 'Any status'}
              </option>
            ))}
          </select>

          <label htmlFor="ops-health-task-limit">Limit</label>
          <input
            id="ops-health-task-limit"
            type="number"
            min="1"
            max="100"
            value={taskFilters.limit}
            onChange={(event) => updateTaskFilter('limit', event.target.value)}
          />

          <label className="ops-health-toggle" htmlFor="ops-health-boosted">
            <input
              id="ops-health-boosted"
              type="checkbox"
              checked={taskFilters.boosted}
              onChange={(event) =>
                updateTaskFilter('boosted', event.target.checked)
              }
            />
            Boosted only
          </label>

          <label className="ops-health-toggle" htmlFor="ops-health-rewarded">
            <input
              id="ops-health-rewarded"
              type="checkbox"
              checked={taskFilters.rewarded}
              onChange={(event) =>
                updateTaskFilter('rewarded', event.target.checked)
              }
            />
            Rewarded only
          </label>

          <label className="ops-health-toggle" htmlFor="ops-health-anomaly-only">
            <input
              id="ops-health-anomaly-only"
              type="checkbox"
              checked={taskFilters.anomalyOnly}
              onChange={(event) =>
                updateTaskFilter('anomalyOnly', event.target.checked)
              }
            />
            Anomaly only
          </label>

          <div className="ops-health-finder-actions">
            <button type="submit" disabled={taskSearchLoading}>
              {taskSearchLoading ? 'Searching...' : 'Search'}
            </button>
            <button
              type="button"
              onClick={() => fetchTaskResults(taskFilters)}
              disabled={taskSearchLoading}
            >
              Refresh
            </button>
          </div>
        </form>
        {taskSearchError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {taskSearchError}
          </div>
        ) : null}
        <div
          className="ops-health-task-results"
          data-testid="ops-health-task-results"
        >
          {taskResults.length > 0 ? (
            taskResults.map((task) => (
              <article
                className="ops-health-task-row"
                key={task.taskId}
                data-testid="ops-health-task-result"
              >
                <div className="ops-health-task-row-main">
                  <strong>{task.title || 'Untitled task'}</strong>
                  <span>{task.taskId}</span>
                </div>
                <div className="ops-health-task-row-meta">
                  <span>{task.status || 'unknown'}</span>
                  <span>creator {task.creatorPlayerId || '--'}</span>
                  <span>helper {task.helperPlayerId || 'unassigned'}</span>
                  <span>updated {formatDate(task.updatedAt)}</span>
                </div>
                <div className="ops-health-task-row-badges">
                  <span>{formatJgk(task.priceJGK)} escrow</span>
                  <span>{task.escrowStatus || 'none'} escrow</span>
                  <span>{task.payoutStatus || 'unpaid'} payout</span>
                  {task.hasBoost ? (
                    <span>{formatJgk(task.platformBoostJGK)} boost</span>
                  ) : null}
                  {task.hasRewards ? (
                    <span>{formatJgk(task.creatorRewardsJGK)} rewards</span>
                  ) : null}
                  <span>{task.anomalyCount || 0} anomalies</span>
                </div>
                <button
                  type="button"
                  onClick={() => loadTaskReport(task.taskId)}
                  disabled={taskLoading}
                >
                  Open accounting report
                </button>
              </article>
            ))
          ) : (
            <p className="ops-health-muted">
              No recent tasks match the current filters.
            </p>
          )}
        </div>
      </section>

      <section className="ops-health-panel">
        <div className="ops-health-section-heading">
          <h2>Advanced: Lookup By Task Id</h2>
          <span>task-level proof</span>
        </div>
        <form className="ops-health-task-form" onSubmit={handleTaskLookup}>
          <label htmlFor="ops-health-task-id">Task id</label>
          <input
            id="ops-health-task-id"
            value={taskId}
            onChange={(event) => setTaskId(event.target.value)}
            placeholder="Paste task ObjectId"
          />
          <button type="submit" disabled={taskLoading}>
            {taskLoading ? 'Checking...' : 'Lookup'}
          </button>
        </form>
        {taskError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {taskError}
          </div>
        ) : null}
        {taskReport ? (
          <div className="ops-health-task-report" data-testid="ops-health-task-report">
            <Metric label="Task id" value={taskReport.taskId || '--'} />
            <Metric label="Escrow" value={formatJgk(taskReport.escrowAmountJGK)} />
            <Metric
              label="Payout"
              value={formatJgk(
                taskReport.expectedHelperPayoutJGK ??
                  taskReport.summary?.helperPayoutJGK,
              )}
            />
            <Metric label="Fee" value={formatJgk(taskReport.netPlatformFeeJGK)} />
            <Metric label="Boost" value={formatJgk(taskReport.platformBoostJGK)} />
            <Metric
              label="Rewards"
              value={formatJgk(taskReport.summary?.creatorRewardsJGK)}
            />
            <Metric
              label="Net platform position"
              value={formatJgk(taskReport.summary?.netPlatformPositionJGK)}
            />
            <Metric
              label="Task anomalies"
              value={(taskReport.anomalies || []).length}
            />
            <JsonDetails
              title="Raw task JSON"
              payload={taskReport}
              testId="ops-health-task-json"
            />
          </div>
        ) : null}
      </section>

      <JsonDetails
        title="Raw readiness and rollup JSON"
        payload={{ readiness, rollup }}
        testId="ops-health-raw-json"
      />
    </main>
  );
}
